import VersionChecker from './VersionChecker';
import Service from './Service';
import {
  getConversations,
  getMessages,
  getLivestreamMessages,
  getMeetingMessages,
  initializeConversations,
  initializeMessages,
  initializeLivestreamMessages,
  initializeMeetingMessages,
} from './Pagination';
import { isMeetingMessageSegment } from '../utils/liveStreamMessageSegments';
import UserAccount from './UserAccount';
import Navigation from './Navigation';
import LiveStreamSession from './LiveStreamSession';
import io from 'socket.io-client';
import Constants from '../submodules/logictry_config/constants';
import LogicBaseConversationCache from './cache/LogicBaseConversationCache';
import LogicBaseMessageCache from './cache/LogicBaseMessageCache';
import Server from './server';
import isCrawler from '../utils/isCrawler';

function applyRemoteMessageEditToCache(cache, messageId, text, editedTime) {
  const id = messageId != null ? String(messageId) : '';
  if (!id || text == null) return;
  const j = cache.objectJson[id];
  const o = cache.objects[id];
  if (!j && !o) return;
  if (j) {
    j.t = text;
    if (editedTime != null) {
      j.et = editedTime instanceof Date ? editedTime : new Date(editedTime);
    } else {
      delete j.et;
    }
  }
  if (o) {
    o.text = text;
    if (editedTime != null) {
      o.editedTime = editedTime instanceof Date ? editedTime : new Date(editedTime);
    } else {
      delete o.editedTime;
    }
  }
  cache.emitStateUpdate();
}

/** Patch LogicBaseMessageCache and nudge the content-thread pagination pager (livestream / meeting). */
function applyMessageEditToAllCaches(messageId, text, editedTime) {
  applyRemoteMessageEditToCache(LogicBaseMessageCache, messageId, text, editedTime);
  notifySegmentMessagePagination(messageId);
}

function applyMessageDeleteToAllCaches(messageId, deletedTime) {
  applyRemoteMessageDeleteToCache(LogicBaseMessageCache, messageId, deletedTime);
  notifySegmentMessagePagination(messageId);
}

function notifySegmentMessagePagination(messageId) {
  const id = messageId != null ? String(messageId) : '';
  const j = LogicBaseMessageCache.objectJson[id];
  const contentId = j?.c != null ? String(j.c) : '';
  if (!contentId) return;
  const segment = j.lsc || null;
  if (segment === 'livestream' || isMeetingMessageSegment(segment)) {
    segmentMessageCache(contentId, segment).emitStateUpdate();
  }
}

function applyRemoteMessageDeleteToCache(cache, messageId, deletedTime) {
  const id = messageId != null ? String(messageId) : '';
  if (!id) return;
  const dt = deletedTime instanceof Date ? deletedTime : new Date(deletedTime);
  const j = cache.objectJson[id];
  const o = cache.objects[id];
  if (!j && !o) return;
  if (j) j.dt = dt;
  if (o) o.deletedTime = dt;
  cache.emitStateUpdate();
}

function mergeConversationRt(rt) {
  if (rt == null) return null;
  const plain = rt instanceof Map ? Object.fromEntries(rt) : rt;
  const out = {};
  Object.keys(plain).forEach((k) => {
    const v = plain[k];
    out[k] = v instanceof Date ? v : new Date(v);
  });
  return out;
}

/** Merge a conversation document from the server into LogicBaseConversationCache and pagination. */
function applyConversationDocToCache(convDoc) {
  const id = convDoc?._id != null ? String(convDoc._id) : '';
  if (!id || convDoc.error) return;

  const me = String(UserAccount.account._id);
  const participantStrings = (convDoc.p || []).map((x) => String(x));
  const otherId = participantStrings.find((pid) => pid !== me);

  let j = LogicBaseConversationCache.objectJson[id];
  const rtMerged = mergeConversationRt(convDoc.rt);

  if (!j) {
    LogicBaseConversationCache.createFromJson(
      {
        _id: convDoc._id,
        ct: convDoc.ct,
        ut: convDoc.ut,
        k: convDoc.k,
        p: convDoc.p,
        rt: rtMerged || {},
      },
      otherId ? { p: otherId } : undefined
    );
    j = LogicBaseConversationCache.objectJson[id];
  } else {
    if (convDoc.ut != null) j.ut = convDoc.ut;
    if (rtMerged) j.rt = { ...(j.rt || {}), ...rtMerged };
    LogicBaseConversationCache.objects[id] = LogicBaseConversationCache.createObject(j);
  }

  const conv = LogicBaseConversationCache.objects[id];
  const conversations = getConversations();
  if (conv && !conversations.currentPage.some((c) => String(c._id) === id)) {
    conversations.addLocalPage(conv);
  }

  LogicBaseConversationCache.emitStateUpdate();
}

/** Route a content-thread message to the pagination cache for its segment (if any). */
function addContentMessageToPagination(contentId, message, messageSegment) {
  if (isMeetingMessageSegment(messageSegment)) {
    getMeetingMessages(contentId).addLocalPage(message);
  } else if (messageSegment === 'livestream') {
    getLivestreamMessages(contentId).addLocalPage(message);
  } else {
    getMessages(contentId).addLocalPage(message);
  }
}

function resolveMessageSegment(result) {
  return result?.messageSegment || result?.message?.lsc || null;
}

function segmentMessageCache(contentId, messageSegment) {
  if (isMeetingMessageSegment(messageSegment)) return getMeetingMessages(contentId);
  if (messageSegment === 'livestream') return getLivestreamMessages(contentId);
  return getMessages(contentId);
}

function clearPendingMessagesInCache(cache, confirmedMessage, clientId = null) {
  if (!cache?.__localPage?.length) return;
  const normalizedClientId = typeof clientId === 'string' && clientId.startsWith('pending:')
    ? clientId
    : null;
  const sender = confirmedMessage?.sender != null
    ? String(confirmedMessage.sender)
    : (confirmedMessage?.s != null ? String(confirmedMessage.s) : null);
  const text = confirmedMessage?.text ?? confirmedMessage?.t;
  let removed = false;
  cache.__localPage = cache.__localPage.filter((o) => {
    if (!String(o?._id || '').startsWith('pending:')) return true;
    if (removed) return true;
    const pendingId = String(o._id);
    const matchesClientId = normalizedClientId && pendingId === normalizedClientId;
    const matchesText = sender && String(o?.sender) === sender && o?.text === text;
    if (matchesClientId || matchesText) {
      removed = true;
      delete LogicBaseMessageCache.objectJson[pendingId];
      delete LogicBaseMessageCache.objects[pendingId];
      return false;
    }
    return true;
  });
}

function clearPendingSegmentMessages(contentId, messageSegment, confirmedMessage, clientId = null) {
  const cache = segmentMessageCache(contentId, messageSegment);
  clearPendingMessagesInCache(cache, confirmedMessage, clientId);
}

function addOptimisticMessage(threadId, text, attachments, messageSegment, contentId) {
  const pendingId = `pending:${Date.now()}:${Math.random().toString(36).slice(2, 9)}`;
  const messageJson = {
    _id: pendingId,
    c: threadId,
    ct: new Date(),
    s: UserAccount.account._id,
    t: text,
    a: attachments,
    ut: new Date(),
    ...(messageSegment ? { lsc: messageSegment } : {}),
  };
  const message = LogicBaseMessageCache.createFromJson(messageJson);
  if (messageSegment || contentId) {
    addContentMessageToPagination(threadId, message, messageSegment);
  } else if (threadId) {
    getMessages(threadId).addLocalPage(message);
  }
  return pendingId;
}

function removeOptimisticMessage(pendingId, threadId, messageSegment) {
  if (!pendingId) return;
  const id = String(pendingId);
  delete LogicBaseMessageCache.objectJson[id];
  delete LogicBaseMessageCache.objects[id];
  const cache = messageSegment
    ? segmentMessageCache(threadId, messageSegment)
    : getMessages(threadId);
  if (cache?.__localPage) {
    cache.__localPage = cache.__localPage.filter((o) => String(o?._id) !== id);
    cache.emitStateUpdate?.();
  }
  LogicBaseMessageCache.emitStateUpdate();
}

function ingestContentMessage(result) {
  const threadId = result?.contentId || result?.collectionId;
  const raw = result?.message;
  const messageId = raw?._id != null ? String(raw._id) : null;
  if (!(threadId && messageId)) return null;

  const segment = resolveMessageSegment(result);
  const messageJson = {
    ...raw,
    _id: messageId,
    ...(raw.c != null ? { c: String(raw.c) } : {}),
    ...(raw.s != null ? { s: String(raw.s) } : {}),
    ...(segment ? { lsc: raw.lsc || segment } : {}),
  };
  const message = LogicBaseMessageCache.createFromJson(messageJson);
  const cache = segment
    ? segmentMessageCache(threadId, segment)
    : getMessages(threadId);
  clearPendingMessagesInCache(cache, message, result?.clientId);
  addContentMessageToPagination(threadId, message, segment);
  LogicBaseMessageCache.emitStateUpdate();
  return message;
}

const socket = Constants.isDevelopment
  ? io(Constants.logictryOrigin, { path: '/socket.io' })
  : io(Constants.logictryOrigin, {
      path: '/socket.io',
      // Websocket-only fails on Safari (desktop + iOS): Engine.IO never
      // connects, so live chat and Messages both show "Could not send".
      // Try websocket first (Chrome/Firefox stay on it); fall back to polling
      // and stay there (`upgrade: false`) so Safari does not loop a broken WS.
      transports: ['websocket', 'polling'],
      upgrade: false,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      timeout: 20000,
      withCredentials: true
    });

class Websockets extends Service {
  liveUserCache = new Map();

  inFlightUsers = new Map();
  batchQueue = new Set();
  batchResolvers = new Map();
  batchTimer = null;

  typing = new Set();

  /**
   * Content rooms currently joined; re-emitted on every (re)connect so pushes
   * resume. The content room (segmented chat delivery + `livestream-update`
   * pushes) has a single client-side owner: `LiveStreamContentStage`, which is
   * an ancestor of every `LiveStreamInlineChat` (meeting + audience) and stays
   * mounted for the whole livestream page — so one join/leave on its lifecycle
   * covers all consumers. (Live-presence counts are a separate URL-based
   * mechanism — see `trackClient` server-side — not this room.)
   */
  joinedContentRooms = new Set();

  joinedCollectionRooms = new Set();

  contentStats = {
    contentId: undefined,
    collectionId: undefined,
    liveUsers: [],
    liveTotalUsers: 0,
  };

  liveStats = {
    liveTotal: 0,
    liveUsers: 0,
    liveUserTotal: 0,
    hasMoreLive: false,
    dauTotal: 0,
    dauUsers: 0,
    users: [],
    usersDailyTotal: 0,
    hasMoreUsersDaily: false,
    usersDaily: [],
    usersDailyByDay: [],
    hasMoreDauDays: false,
    content: [],
    contentTotal: 0,
    hasMoreContent: false,
    collections: [],
    collectionTotal: 0,
    hasMoreCollections: false,
  };

  setLiveStats(stats) {
    this.liveStats = stats;
    this.emitStateUpdate();
  }

  sendMessage(recipientId, text, conversationId, collectionId, attachments = [], contentId, messageSegment) {
    if (!(recipientId || collectionId || contentId)) {
      return Promise.resolve({ success: false, error: 'InvalidTarget' });
    }

    const trimmed = (text || '').trim();
    const normalizedAttachments = (attachments || [])
      .map(({ url, type }) => ({ url: url?.trim(), type: type || 'image' }))
      .filter((a) => a.url);

    if (!trimmed && !normalizedAttachments.length) {
      return Promise.resolve({ success: false, error: 'EmptyMessage' });
    }

    const threadId = conversationId || contentId || collectionId;
    const segmentedContent = contentId && messageSegment;
    const pendingId = threadId
      ? addOptimisticMessage(threadId, trimmed, normalizedAttachments, segmentedContent ? messageSegment : null, contentId)
      : null;

    return new Promise((resolve) => {
      socketSendMessage("message-send", {
        recipientId,
        conversationId,
        collectionId,
        contentId,
        text: trimmed,
        attachments: normalizedAttachments,
        messageSegment,
        clientId: pendingId || undefined,
      }, (res) => {
        if (!res?.success) {
          removeOptimisticMessage(
            pendingId,
            threadId,
            segmentedContent ? messageSegment : null,
          );
        }
        resolve({
          success: !!res?.success,
          error: res?.error || (res?.success ? undefined : 'SendFailed'),
          retryAfterMs: res?.retryAfterMs,
        });
      });
    });
  }

  editMessage(messageId, text) {
    if (!messageId || !text?.trim()) {
      return Promise.resolve({ success: false, error: 'EmptyMessage' });
    }

    const id = String(messageId);
    const prevJson = LogicBaseMessageCache.objectJson[id];
    const prevObject = LogicBaseMessageCache.objects[id];
    const prevText = prevJson?.t ?? prevObject?.text;
    const prevEditedTime = prevJson?.et ?? prevObject?.editedTime;
    const trimmed = text.trim();

    applyMessageEditToAllCaches(id, trimmed, new Date());

    return new Promise((resolve) => {
      socketSendMessage("message-edit", {
        messageId,
        text: trimmed,
      }, (res) => {
        if (!res?.success && prevText != null) {
          applyMessageEditToAllCaches(
            id,
            prevText,
            prevEditedTime != null ? new Date(prevEditedTime) : null,
          );
        }
        resolve({
          success: !!res?.success,
          error: res?.error || (res?.success ? undefined : 'EditFailed'),
        });
      });
    });
  }

  deleteMessage(messageId) {
    if (!messageId) return;

    const id = String(messageId);
    applyMessageDeleteToAllCaches(id, new Date());

    socketSendMessage("message-delete", { messageId }, (res) => {
      if (!res?.success) {
        const j = LogicBaseMessageCache.objectJson[id];
        if (j) delete j.dt;
        const o = LogicBaseMessageCache.objects[id];
        if (o) delete o.deletedTime;
        LogicBaseMessageCache.emitStateUpdate();
        notifySegmentMessagePagination(id);
      }
    });
  }

  getLiveEntity({ contentId, collectionId, offset = 0, limit = 9 }) {
    return new Promise((resolve) => {
      socketSendMessage(
        "get-live-entity",
        { contentId, collectionId, offset, limit },
        resolve
      );
    });
  }

  getDauEntity({
    contentId,
    collectionId,
    date,
    userId,
    days,
    offset,
    limit,
    visitorOffset,
    visitorLimit,
    live,
  } = {}) {
    return new Promise((resolve) => {
      socketSendMessage(
        "get-dau-entity",
        {
          contentId,
          collectionId,
          date,
          userId,
          days,
          offset,
          limit,
          visitorOffset,
          visitorLimit,
          live,
        },
        resolve
      );
    });
  }

  getLiveStats(pageOpts = {}) {
    return new Promise((resolve) => {
      socketSendMessage("get-live-stats", pageOpts, resolve);
    });
  }

  fetchUsersLive(ids = []) {
    return new Promise((resolve) => {
      socketSendMessage("is-user-live", { ids }, (res) => {
        resolve(res?.users || []);
      });
    });
  }

  sendCollectionUpdate(collectionId, live) {
    if (collectionId) {
      if (live) this.joinedCollectionRooms.add(String(collectionId));
      else this.joinedCollectionRooms.delete(String(collectionId));
    }
    socketSendMessage('update-collection', { collectionId, live });
  }

  sendContentUpdate(contentId, live) {
    if (contentId) {
      if (live) this.joinedContentRooms.add(String(contentId));
      else this.joinedContentRooms.delete(String(contentId));
    }
    socketSendMessage('update-content', { contentId, live });
  }

  /**
   * Re-join every tracked room after a reconnect. socket.io rooms are per
   * connection, so a new socket starts in no rooms; without this, realtime
   * pushes (chat + livestream-update) silently stop after any reconnect.
   */
  rejoinRooms() {
    this.joinedContentRooms.forEach((contentId) => {
      socketSendMessage('update-content', { contentId, live: true });
    });
    this.joinedCollectionRooms.forEach((collectionId) => {
      socketSendMessage('update-collection', { collectionId, live: true });
    });
  }

  sendTypingUpdate(recipientId, conversationId, typing) {
    if (this.lastTypingUpdate?.conversationId === conversationId && this.lastTypingUpdate?.typing === typing) return;
    if (this.lastTypingUpdate?.conversationId && this.lastTypingUpdate.conversationId !== conversationId) {
      this.lastTypingUpdate.typing = false;
      socketSendMessage('update-typing', this.lastTypingUpdate);
    }
    this.lastTypingUpdate = { recipientId, conversationId, typing };
    socketSendMessage('update-typing', this.lastTypingUpdate);
  }

  queueBatchFetch(userIds = []) {
    return new Promise((resolve) => {
      for (const userId of userIds) {
        if (this.inFlightUsers.has(userId)) continue;

        if (!this.batchResolvers.has(userId)) {
          this.batchResolvers.set(userId, []);
        }

        this.batchResolvers.get(userId).push(resolve);
        this.batchQueue.add(userId);
      }

      if (!this.batchTimer) {
        this.batchTimer = setTimeout(() => {
          const ids = [...this.batchQueue];
          this.batchQueue.clear();
          this.batchTimer = null;

          if (!ids.length) return;

          const promise = this.fetchUsersLive(ids).then((res) => {
            const now = Date.now();
            const map = new Map();

            for (const { userId, isLive } of res) {
              this.liveUserCache.set(userId, { isLive, ts: now });
              map.set(userId, isLive);
            }

            for (const id of ids) {
              if (!map.has(id)) {
                map.set(id, false);
                this.liveUserCache.set(id, { isLive: false, ts: now });
              }
            }

            for (const id of ids) {
              const resolvers = this.batchResolvers.get(id) || [];
              for (const r of resolvers) r(map);
              this.batchResolvers.delete(id);
              this.inFlightUsers.delete(id);
            }

            return map;
          });

          for (const id of ids) {
            this.inFlightUsers.set(id, promise);
          }
        }, 10);
      }
    });
  }

  getUsersLiveCached(userIds = []) {
    const now = Date.now();
    const result = {};
    const missing = [];

    userIds = [...new Set(userIds)];

    for (const userId of userIds) {
      const cached = this.liveUserCache.get(userId);

      if (cached && (now - cached.ts) <= 10000) {
        result[userId] = cached.isLive;
      } else {
        missing.push(userId);
      }
    }

    if (missing.length) {
      this.queueBatchFetch(missing).then(() => {
        this.emitStateUpdate(); // just trigger re-render, don't mutate stale result
      });
    }

    for (const id of userIds) {
      if (result[id] === undefined) {
        const cached = this.liveUserCache.get(id);
        if (cached) {
          result[id] = cached.isLive; // preserve last known state
        } else {
          result[id] = false;
        }
      }
    }

    return result;
  }

  async isUsersLive(userIds = []) {
    return this.getUsersLiveCached(userIds);
  }

  async fetchLiveEntityStats({ contentId, collectionId } = {}) {
    const prev = this.contentStats;
    if (prev.contentId !== contentId || prev.collectionId !== collectionId) {
      this.contentStats = {
        contentId,
        collectionId,
        liveUsers: [],
        liveTotalUsers: 0,
      };
    }

    if (!contentId && !collectionId) return;

    if (!UserAccount.isLoggedIn || UserAccount.account?.privacySettings?.disableLiveActivity) {
      this.clearLiveEntityStats();
      return;
    }

    try {
      // Page poll races update-client-info: Redis has not written this visitor yet,
      // so one other person returns liveTotal === 1 and the facepile stays hidden.
      await ensureCurrentPresence();
      const res = await this.getLiveEntity({ contentId, collectionId });

      let liveUsers = [];
      let liveTotalUsers = 0;
      if (contentId && res?.content) {
        liveUsers = res.content.users || [];
        liveTotalUsers = res.content.liveTotal ?? 0;
      } else if (collectionId && res?.collection) {
        liveUsers = res.collection.users || [];
        liveTotalUsers = res.collection.liveTotal ?? 0;
      } else {
        return;
      }

      this.contentStats = {
        contentId,
        collectionId,
        liveUsers,
        liveTotalUsers,
      };
      this.emitStateUpdate();
    } catch {
      // Ignore transient poll failures; next interval will retry.
    }
  }

  clearLiveEntityStats() {
    this.contentStats = {
      contentId: undefined,
      collectionId: undefined,
      liveUsers: [],
      liveTotalUsers: 0,
    };
    this.emitStateUpdate();
  }
}

async function sendClientInfo() {
  if (typeof navigator !== 'undefined' && isCrawler(navigator.userAgent)) return { success: true };
  if (!UserAccount.isLoggedIn || !Server.getWebsocketToken() || reloading) {
    lastAckedHref = null;
    return { success: true, skipped: true };
  }
  if (!socket.connected) {
    socket.connect();
    return { success: false, skipped: true };
  }
  const { href } = parsePresenceFromLocation();
  const accountKey = accountPresenceKey();
  const connectionGeneration = presenceConnectionGeneration;
  return new Promise((resolve) => {
    socketSendMessage('update-client-info', { href }, (res) => {
      if (connectionGeneration !== presenceConnectionGeneration || accountKey !== accountPresenceKey()) {
        resolve({ success: false, stale: true });
        return;
      }
      if (res?.refreshRequired) {
        reloadPage();
        resolve(res);
        return;
      }
      if (res?.success) {
        lastAccountPresenceKey = accountKey;
        lastAckedHref = UserAccount.account?.privacySettings?.disableLiveActivity ? null : href;
      }
      resolve(res || { success: false });
    });
  });
}

function parsePresenceFromLocation() {
  const href = typeof window !== "undefined" ? window.location.href : "";
  let contentId;
  let collectionId;
  try {
    const url = new URL(href);
    contentId = url.searchParams.get("content");
    collectionId = url.searchParams.get("collection");
    const pathParts = url.pathname.split("/").filter(Boolean);
    if (!contentId && pathParts[0] === "content") {
      contentId = pathParts[1];
    }
    if (!collectionId && pathParts[0] === "collections") {
      collectionId = pathParts[1];
    }
  } catch {
    //
  }
  return { href, contentId, collectionId };
}

const PRESENCE_ROUTE_CHECK_MS = 1000;
const PRESENCE_HEARTBEAT_WHEN_STILL_MS = 5000;

let lastPresenceHref = null;
let lastSendTime = 0;
let lastAckedHref = null;
let inFlightPresence = null;
let presenceConnectionGeneration = 0;

function doPresenceUpdate() {
  if (inFlightPresence) return inFlightPresence;
  const { href } = parsePresenceFromLocation();
  lastSendTime = Date.now();
  lastPresenceHref = href;
  const pending = sendClientInfo();
  inFlightPresence = pending;
  pending.finally(() => {
    if (inFlightPresence === pending) inFlightPresence = null;
  });
  return pending;
}

/** Wait until this signed-in account's presence update is acknowledged. */
async function ensureCurrentPresence() {
  if (typeof navigator !== 'undefined' && isCrawler(navigator.userAgent)) return;
  if (!UserAccount.isLoggedIn || UserAccount.account?.privacySettings?.disableLiveActivity) return;
  const { href, contentId, collectionId } = parsePresenceFromLocation();
  if (!contentId && !collectionId) return;
  if (lastAckedHref === href) return;
  if (inFlightPresence) {
    await inFlightPresence;
    if (lastAckedHref === href) return;
  }
  await doPresenceUpdate();
}

function onPresenceIntervalTick() {
  const { href } = parsePresenceFromLocation();
  const now = Date.now();
  const routeChanged = lastPresenceHref == null || lastPresenceHref !== href;
  const needHeartbeat =
    !lastSendTime || now - lastSendTime >= PRESENCE_HEARTBEAT_WHEN_STILL_MS;
  if (routeChanged || needHeartbeat) {
    doPresenceUpdate();
  }
}

const SOCKET_ACK_TIMEOUT_MS = 10000;
const SOCKET_ACK_DEADLINE_MS = 15000;

async function socketSendMessage(event, message, callback) {
  reconnectAfterBackground();
  const websocketToken = Server.getWebsocketToken();
  if (websocketToken) message.websocketToken = websocketToken;

  let settled = false;
  const finish = (res) => {
    if (settled) return;
    settled = true;
    clearTimeout(deadlineTimer);
    callback?.(res);
  };

  const deadlineTimer = setTimeout(() => finish({ success: false }), SOCKET_ACK_DEADLINE_MS);

  const send = () => {
    if (settled) return;
    message.websocketToken = Server.getWebsocketToken();
    socket.timeout(SOCKET_ACK_TIMEOUT_MS).emit(event, message, (err, res) => {
      if (err) {
        socket.connect();
        return finish({ success: false });
      }
      finish(res);
    });
  };

  if (!socket.connected) {
    socket.connect();
    socket.once("connect", send);
  } else {
    send();
  }
}

const singleton = new Websockets();

function reconnectAfterBackground() {
  // Reconnect if the transport dropped for any reason (not tab visibility — we stay connected in background).
  if (typeof document !== 'undefined' && !socket.connected) {
    socket.connect();
  }
}

// One reload per document. The heartbeat ack, the room event, and the account check can
// all ask within the same second; a second reload() call restarts the navigation.
let reloading = false;
function reloadPage() {
  if (reloading) return;
  reloading = true;
  window.location.reload();
}

const ACCOUNT_CHECK_MS = 5 * 60 * 1000;
const ACCOUNT_CHECK_COOLDOWN_MS = 30 * 1000;
let lastAccountCheckAt = 0;
let trailingAccountCheck = null;

function scheduleAccountCheck() {
  const elapsed = Date.now() - lastAccountCheckAt;
  if (lastAccountCheckAt && elapsed < ACCOUNT_CHECK_COOLDOWN_MS) {
    if (trailingAccountCheck) return;
    trailingAccountCheck = setTimeout(() => {
      trailingAccountCheck = null;
      checkAccountStillCurrent();
    }, ACCOUNT_CHECK_COOLDOWN_MS - elapsed);
    return;
  }
  checkAccountStillCurrent();
}

async function checkAccountStillCurrent() {
  if (trailingAccountCheck) {
    clearTimeout(trailingAccountCheck);
    trailingAccountCheck = null;
  }
  if (!UserAccount.isLoggedIn || UserAccount.isLoggingOut || reloading) return;
  lastAccountCheckAt = Date.now();
  try {
    const account = await Server.checkAuth();
    // A duplicate in-flight /auth or a network error is not an answer.
    if (!account || account instanceof Error || account.error) return;
    if (!UserAccount.isLoggedIn || UserAccount.isLoggingOut) return;
    if (!account._id || !account.loggedIn || UserAccount.claimsChanged(account)) reloadPage();
  } catch {
    // A failed check waits for the next foreground or the five-minute interval.
  }
}

document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') {
    scheduleAccountCheck();
    reconnectAfterBackground();
  }
});
window.addEventListener('pageshow', () => {
  scheduleAccountCheck();
});
setInterval(scheduleAccountCheck, ACCOUNT_CHECK_MS);

/**
 * Heavy resync after a real reconnect: refetch everything that could have missed
 * a push while the socket was down — message caches and live-stream state. Room
 * rejoin is handled separately (always, cheaply) in the connect handler.
 */
function refetchAfterReconnect() {
  initializeConversations();
  initializeMessages();
  initializeLivestreamMessages();
  initializeMeetingMessages();
  LogicBaseConversationCache.emitStateUpdate();
  singleton.typing.clear();
  singleton.emitStateUpdate();
  LiveStreamSession.reloadAll();
}

// The connection — not tab visibility — is the true signal for "did I miss
// realtime?". socket.io v4 fires `connect` on the initial connection AND every
// reconnection (the manager-level `reconnect` event does not fire on the socket,
// so the old `socket.on("reconnect")` was dead), so this is the single hook for
// all cache invalidation + room rejoin + presence.
let lastAccountPresenceKey = null;
function accountPresenceKey() {
  if (!UserAccount.isLoggedIn) return null;
  return JSON.stringify([
    UserAccount.account?._id,
    Server.getWebsocketToken(),
    !!UserAccount.account?.privacySettings?.disableLiveActivity,
  ]);
}

socket.on('refresh-required', () => {
  if (UserAccount.isLoggedIn && !UserAccount.isLoggingOut) reloadPage();
});

let hasConnectedOnce = false;
socket.on("disconnect", () => {
  presenceConnectionGeneration += 1;
  inFlightPresence = null;
  lastAccountPresenceKey = null;
  lastAckedHref = null;
});

socket.on("connect", () => {
  presenceConnectionGeneration += 1;
  inFlightPresence = null;
  lastAckedHref = null;
  lastAccountPresenceKey = null;
  doPresenceUpdate();
  // Always re-join rooms (cheap, idempotent) so pushes resume after any drop —
  // including a brief background that disconnected the socket.
  singleton.rejoinRooms();
  // Skip the heavy refetch only on the very FIRST connect (page load) — mounting
  // components do their own initial load. On every RECONNECT, refetch
  // unconditionally with NO debounce: a reconnect means the socket actually
  // dropped, so a message could have been missed while it was down. The previous
  // time-based debounce could SKIP the reconnect refetch (e.g. a quick
  // background → message-sent-by-someone-else → foreground reconnect), which left
  // the chat caches stale — the missed message never came through and chat
  // stopped updating on that client. socket.io spaces reconnects by
  // `reconnectionDelay` (≥1s), so an unconditional refetch per reconnect is safe.
  if (!hasConnectedOnce) {
    hasConnectedOnce = true;
    return;
  }
  refetchAfterReconnect();
});

// Instant live-stream lifecycle updates (go live, end, replay ready, camera
// lock) pushed to everyone in the content room — replaces blind polling.
socket.on('livestream-update', (payload) => {
  if (!payload?.contentId || !payload?.stream) return;
  LiveStreamSession.applyRealtimeUpdate(payload.contentId, payload.stream);
});

socket.on('livestream-camctrl', (payload) => {
  if (!payload?.contentId) return;
  LiveStreamSession.applyCamControl(payload.contentId, payload);
});

socket.on('server-version', (msg) => {
  VersionChecker.updateVersion(msg);
});

socket.on('server-live-stats', (stats) => {
  singleton.setLiveStats(stats);
});

socket.on("message-sent", (result) => {
  const threadId = result?.conversation?._id || result?.contentId || result?.collectionId;
  if (!(threadId && result?.message?._id)) return;

  if (result.conversation) {
    applyConversationDocToCache(result.conversation);
  }

  if (result.contentId || result.collectionId) {
    ingestContentMessage(result);
  } else {
    const message = LogicBaseMessageCache.createFromJson(result.message);
    clearPendingMessagesInCache(getMessages(threadId), message, result?.clientId);
    getMessages(threadId).addLocalPage(message);
  }

  singleton.emitStateUpdate();
});

socket.on("message-edited", ({ messageId, text, editedTime }) => {
  applyMessageEditToAllCaches(messageId, text, editedTime);
});

socket.on("message-deleted", ({ messageId, deletedTime }) => {
  applyMessageDeleteToAllCaches(messageId, deletedTime);
});

socket.on("typing-update", ({ conversationId, typing }) => {
  if (typing) singleton.typing.add(conversationId);
  else singleton.typing.delete(conversationId);
  singleton.emitStateUpdate();
});

setInterval(onPresenceIntervalTick, PRESENCE_ROUTE_CHECK_MS);
onPresenceIntervalTick();

let navTimeout;
Navigation.onStateUpdate(() => {
  clearTimeout(navTimeout);
  navTimeout = setTimeout(() => {
    const { href } = parsePresenceFromLocation();
    if (lastPresenceHref == null || lastPresenceHref !== href) {
      doPresenceUpdate();
    }
  }, 100);
});

UserAccount.onStateUpdate(() => {
  // Account notifications also cover unrelated UI changes; only sync changed identity,
  // token, or activity preference here. Regular heartbeats stay on the existing timer.
  const key = accountPresenceKey();
  if (key && key !== lastAccountPresenceKey) doPresenceUpdate();
});

export default singleton;
